import { supabase } from '../supabase';
import { Species, Exporter_Species } from '../utils/types';

// Get all species
export const getAllSpecies = async (): Promise<Species[]> => {
  try {
    const { data, error } = await supabase
      .from('species')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    return data as Species[];
  } catch (error) {
    console.error('Error fetching species:', error);
    throw error;
  }
};

// Get all species with id and excel_name mapping
export const getSpeciesIdExcelNameMap = async (): Promise<
  Record<string, number>
> => {
  try {
    const { data, error } = await supabase
      .from('species')
      .select('id, excel_name');

    if (error) {
      throw new Error(error.message);
    }

    // Create a map of excel_name (uppercase) to id
    const speciesMap: Record<string, number> = {};
    data.forEach((species) => {
      if (species.excel_name) {
        speciesMap[species.excel_name.toUpperCase()] = species.id;
      }
    });

    return speciesMap;
  } catch (error) {
    console.error('Error fetching species map:', error);
    throw error;
  }
};

// Get species by excel_name
export const getSpeciesByExcelName = async (
  excelName: string
): Promise<Species | null> => {
  try {
    const { data, error } = await supabase
      .from('species')
      .select('*')
      .ilike('excel_name', excelName)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No data found, return null
        return null;
      }
      throw new Error(error.message);
    }

    return data as Species;
  } catch (error) {
    console.error('Error fetching species by excel_name:', error);
    throw error;
  }
};

// Get species by ID
export const getSpeciesById = async (id: number): Promise<Species> => {
  try {
    const { data, error } = await supabase
      .from('species')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return data as Species;
  } catch (error) {
    console.error('Error fetching species:', error);
    throw error;
  }
};

// Get all species for a specific exporter
export const getExporterSpecies = async (
  exporterId: number
): Promise<Species[]> => {
  try {
    const { data, error } = await supabase
      .from('exporters_species')
      .select('species_id, species:species_id(*)')
      .eq('exporter_id', exporterId);

    if (error) {
      throw new Error(error.message);
    }

    // Map the result to get just the species objects
    return data.map((item) => item.species) as unknown as Species[];
  } catch (error) {
    console.error('Error fetching exporter species:', error);
    throw error;
  }
};

// Add a species to an exporter
export const addExporterSpecies = async (
  exporterId: number,
  speciesId: number
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('exporters_species')
      .insert({ exporter_id: exporterId, species_id: speciesId });

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error adding species to exporter:', error);
    throw error;
  }
};

// Remove a species from an exporter
export const removeExporterSpecies = async (
  exporterId: number,
  speciesId: number
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('exporters_species')
      .delete()
      .eq('exporter_id', exporterId)
      .eq('species_id', speciesId);

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error removing species from exporter:', error);
    throw error;
  }
};

// Update all species for an exporter (only update what has changed)
export const updateExporterSpecies = async (
  exporterId: number,
  speciesIds: number[]
): Promise<void> => {
  try {
    console.log(`Actualizando especies para exportador ID: ${exporterId}`);
    console.log('IDs de especies a asignar:', speciesIds);

    // Primero, obtener las especies actualmente asignadas al exportador
    const { data: currentSpeciesData, error: fetchError } = await supabase
      .from('exporters_species')
      .select('species_id')
      .eq('exporter_id', exporterId);

    if (fetchError) {
      console.error('Error al obtener especies actuales:', fetchError);
      throw new Error(fetchError.message);
    }

    // Convertir los datos actuales a un conjunto de IDs para fácil comparación
    const currentSpeciesIds = currentSpeciesData.map((item) => item.species_id);
    console.log('Especies actuales:', currentSpeciesIds);

    // Especies a eliminar (están en las actuales pero no en las nuevas)
    const speciesToRemove = currentSpeciesIds.filter(
      (id) => !speciesIds.includes(id)
    );
    console.log('Especies a eliminar:', speciesToRemove);

    // Especies a agregar (están en las nuevas pero no en las actuales)
    const speciesToAdd = speciesIds.filter(
      (id) => !currentSpeciesIds.includes(id)
    );
    console.log('Especies a agregar:', speciesToAdd);

    // Eliminar especies que ya no están seleccionadas
    if (speciesToRemove.length > 0) {
      for (const speciesId of speciesToRemove) {
        const { error: deleteError } = await supabase
          .from('exporters_species')
          .delete()
          .eq('exporter_id', exporterId)
          .eq('species_id', speciesId);

        if (deleteError) {
          console.error(`Error al eliminar especie ${speciesId}:`, deleteError);
          throw new Error(deleteError.message);
        }
      }
      console.log('Especies eliminadas correctamente');
    }

    // Agregar especies nuevas
    if (speciesToAdd.length > 0) {
      const speciesToInsert = speciesToAdd.map((speciesId) => ({
        exporter_id: exporterId,
        species_id: speciesId,
      }));

      console.log('Insertando nuevas relaciones:', speciesToInsert);

      const { data: insertedData, error: insertError } = await supabase
        .from('exporters_species')
        .insert(speciesToInsert);

      if (insertError) {
        console.error('Error al insertar nuevas especies:', insertError);
        throw new Error(insertError.message);
      }

      console.log('Especies nuevas agregadas correctamente:', insertedData);
    } else {
      console.log('No hay especies nuevas para agregar');
    }

    console.log('Actualización de especies completada con éxito');
  } catch (error) {
    console.error('Error updating exporter species:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Grupos de especie
//
// Se manejan por RPC y no escribiendo species.groups directamente, por dos
// motivos (ver migración 20260803120000_species_group_rpcs.sql):
//  - renombrar/eliminar tiene que arrastrar retail_target_skus.species_group,
//    si no quedan SKUs apuntando a un grupo inexistente;
//  - el rol document_helper necesita gestionar grupos sin tener UPDATE sobre
//    el resto de la tabla species.
// ---------------------------------------------------------------------------

/** Agrega un grupo a la especie. Devuelve la lista actualizada. */
export const addSpeciesGroup = async (
  speciesId: number,
  group: string
): Promise<string[]> => {
  const { data, error } = await supabase.rpc('add_species_group', {
    p_species_id: speciesId,
    p_group: group,
  });

  if (error) throw new Error(error.message);
  return (data as string[]) ?? [];
};

/** Renombra un grupo y arrastra los SKUs que lo usaban. */
export const renameSpeciesGroup = async (
  speciesId: number,
  oldGroup: string,
  newGroup: string
): Promise<string[]> => {
  const { data, error } = await supabase.rpc('rename_species_group', {
    p_species_id: speciesId,
    p_old_group: oldGroup,
    p_new_group: newGroup,
  });

  if (error) throw new Error(error.message);
  return (data as string[]) ?? [];
};

/** Elimina un grupo. Falla si está en uso y quien llama no es superadmin. */
export const removeSpeciesGroup = async (
  speciesId: number,
  group: string
): Promise<string[]> => {
  const { data, error } = await supabase.rpc('remove_species_group', {
    p_species_id: speciesId,
    p_group: group,
  });

  if (error) throw new Error(error.message);
  return (data as string[]) ?? [];
};
